// TypeScript port of engine/probability_engine.py — runs client-side on the
// preloaded track DB. Faithful to the Python semantics (verified by parity
// tests in probability.test.ts) with ONE intentional change: the 2-box case is
// unified as `conditionalChain(boxA, boxB)` and therefore enforces temporal
// ordering (A before B), matching the 3-box chain. The legacy Python 2-box
// `conditional_probability` did an unordered set intersection.

import type {
  Box,
  ClassBreakdown,
  Filters,
  Storm,
  TransitInfo,
} from "./types";

// ── primitives ────────────────────────────────────────────────────────
export function pointInBox(lat: number, lon: number, box: Box): boolean {
  return (
    box.lat_min <= lat &&
    lat <= box.lat_max &&
    box.lon_min <= lon &&
    lon <= box.lon_max
  );
}

function getTransitInfo(storm: Storm, box: Box): TransitInfo | null {
  const inBox = storm.track.filter((p) => pointInBox(p.lat, p.lon, box));
  if (inBox.length === 0) return null;

  let peakV = -Infinity;
  let minP = Infinity;
  for (const p of inBox) {
    if (p.vmax > peakV) peakV = p.vmax;
    if (p.pmin < minP) minP = p.pmin;
  }

  return {
    storm_id: storm.storm_id,
    name: storm.name,
    year: storm.year,
    peak_vmax: storm.peak_vmax,
    min_pmin: storm.min_pmin,
    classification: storm.classification,
    ss_category: storm.ss_category,
    peak_vmax_in_box: peakV,
    min_pmin_in_box: minP,
    num_fixes_in_box: inBox.length,
    entry_dt: inBox[0].dt,
    exit_dt: inBox[inBox.length - 1].dt,
    enso_state: storm.enso_state ?? "Unknown",
    oni_anomaly: storm.oni_anomaly,
  };
}

function firstEntryDt(storm: Storm, box: Box): string | null {
  for (const p of storm.track) {
    if (pointInBox(p.lat, p.lon, box)) return p.dt;
  }
  return null;
}

function passesFilters(storm: Storm, f: Filters): boolean {
  if (f.yearMin != null && storm.year < f.yearMin) return false;
  if (f.yearMax != null && storm.year > f.yearMax) return false;

  if (f.oniRange != null) {
    const v = storm.oni_anomaly;
    if (v == null) return false; // no ONI data (pre-1950)
    if (!(f.oniRange[0] <= v && v <= f.oniRange[1])) return false;
  }

  if (f.monthFilter && f.monthFilter.length > 0) {
    // A storm with an unknown peak month cannot satisfy a month condition —
    // excluding it keeps the conditioned base rate honest. (Mirrors Python.)
    const m = storm.peak_month;
    if (m == null || !f.monthFilter.includes(m)) return false;
  }
  return true;
}

function emptyBreakdown(): ClassBreakdown {
  return { HU: 0, TS: 0, TD: 0 };
}

function tallyBreakdown(rows: TransitInfo[]): ClassBreakdown {
  const b = emptyBreakdown();
  for (const r of rows) {
    if (r.classification === "HU") b.HU++;
    else if (r.classification === "TS") b.TS++;
    else if (r.classification === "TD") b.TD++;
  }
  return b;
}

function effectiveYears(
  rows: TransitInfo[],
  f: Filters,
): number {
  if (f.qualifyingYears != null) return f.qualifyingYears;
  if (rows.length === 0) {
    // No transits: fall back to the filter span so any frequency computed
    // against this denominator is per-year of the queried window.
    if (f.yearMin != null && f.yearMax != null && f.yearMax >= f.yearMin) {
      return f.yearMax - f.yearMin + 1;
    }
    return 1;
  }
  const years = rows.map((r) => r.year);
  const effMin = f.yearMin ?? Math.min(...years);
  const effMax = f.yearMax ?? Math.max(...years);
  return effMax - effMin + 1;
}

function round(n: number, dp: number): number {
  const m = 10 ** dp;
  return Math.round(n * m) / m;
}

// ── public: single-box transit ────────────────────────────────────────
export function stormsThroughBox(
  tracks: Storm[],
  box: Box,
  f: Filters = {},
): TransitInfo[] {
  const results: TransitInfo[] = [];
  for (const storm of tracks) {
    if (!passesFilters(storm, f)) continue;
    const info = getTransitInfo(storm, box);
    if (info) results.push(info);
  }
  results.sort((a, b) => b.year - a.year);
  return results;
}

export interface BoxSummary {
  total: number;
  hurricanes: number;
  tropical_storms: number;
  tropical_depressions: number;
  storms: TransitInfo[];
  monthly_distribution: Record<string, number>;
  annual_frequency: number;
  peak_storm: {
    name: string;
    year: number;
    peak_vmax: number;
    min_pmin: number;
    ss_category: string;
  } | null;
}

const MONTH_NAMES: Record<number, string> = {
  1: "Jan", 2: "Feb", 3: "Mar", 4: "Apr", 5: "May", 6: "Jun",
  7: "Jul", 8: "Aug", 9: "Sep", 10: "Oct", 11: "Nov", 12: "Dec",
};

export function summarizeBox(
  tracks: Storm[],
  box: Box,
  f: Filters = {},
): BoxSummary {
  const transits = stormsThroughBox(tracks, box, f);

  if (transits.length === 0) {
    return {
      total: 0,
      hurricanes: 0,
      tropical_storms: 0,
      tropical_depressions: 0,
      storms: [],
      monthly_distribution: {},
      annual_frequency: 0,
      peak_storm: null,
    };
  }

  const b = tallyBreakdown(transits);

  // Monthly distribution by entry month
  const months: Record<number, number> = {};
  for (const t of transits) {
    const mm = parseInt(t.entry_dt.slice(5, 7), 10);
    if (!Number.isNaN(mm)) months[mm] = (months[mm] ?? 0) + 1;
  }
  const monthly: Record<string, number> = {};
  for (const m of Object.keys(months).map(Number).sort((x, y) => x - y)) {
    monthly[MONTH_NAMES[m] ?? String(m)] = months[m];
  }

  const effYears = effectiveYears(transits, f);
  const annualFreq = effYears > 0 ? transits.length / effYears : 0;

  let peak = transits[0];
  for (const t of transits) if (t.peak_vmax > peak.peak_vmax) peak = t;

  return {
    total: transits.length,
    hurricanes: b.HU,
    tropical_storms: b.TS,
    tropical_depressions: b.TD,
    storms: transits,
    monthly_distribution: monthly,
    annual_frequency: round(annualFreq, 2),
    peak_storm: {
      name: peak.name,
      year: peak.year,
      peak_vmax: peak.peak_vmax,
      min_pmin: peak.min_pmin,
      ss_category: peak.ss_category,
    },
  };
}

// ── public: ordered conditional chain (2 or 3 boxes) ──────────────────
export interface ChainResult {
  prob_b_given_a: number;
  prob_b_given_a_pct: number;
  prob_c_given_ab: number | null;
  prob_c_given_ab_pct: number | null;
  count_a: number;
  count_ab: number;
  count_abc: number | null;
  storms_a: TransitInfo[];
  storms_ab: TransitInfo[];
  storms_abc: TransitInfo[] | null;
  breakdown_a: ClassBreakdown;
  breakdown_ab: ClassBreakdown;
  breakdown_abc: ClassBreakdown | null;
  annual_frequency_a: number;
  annual_frequency_ab: number;
  annual_frequency_abc: number | null;
}

/**
 * P(B|A) and (optionally) P(C|A∩B) with temporal ordering enforced:
 * a storm must enter A before B, and B before C. Pass only boxA+boxB for the
 * 2-leg case; add boxC for the 3-leg chain.
 */
export function conditionalChain(
  tracks: Storm[],
  boxA: Box,
  boxB: Box,
  boxC: Box | null,
  f: Filters = {},
): ChainResult {
  const filtered = tracks.filter((s) => passesFilters(s, f));

  // Leg A
  const stormsA: TransitInfo[] = [];
  for (const s of filtered) {
    const info = getTransitInfo(s, boxA);
    if (info) stormsA.push(info);
  }
  const idsA = new Set(stormsA.map((s) => s.storm_id));

  // Leg A→B (A before B)
  const stormsAB: TransitInfo[] = [];
  for (const s of filtered) {
    if (!idsA.has(s.storm_id)) continue;
    const eA = firstEntryDt(s, boxA);
    const eB = firstEntryDt(s, boxB);
    if (eA == null || eB == null) continue;
    if (eB <= eA) continue;
    const infoB = getTransitInfo(s, boxB);
    if (infoB) {
      infoB.entry_a_dt = eA;
      stormsAB.push(infoB);
    }
  }
  const idsAB = new Set(stormsAB.map((s) => s.storm_id));
  const entryBLookup = new Map(stormsAB.map((s) => [s.storm_id, s.entry_dt]));

  // Leg A→B→C (B before C) — only when boxC provided
  let stormsABC: TransitInfo[] | null = null;
  if (boxC) {
    stormsABC = [];
    for (const s of filtered) {
      if (!idsAB.has(s.storm_id)) continue;
      const eC = firstEntryDt(s, boxC);
      if (eC == null) continue;
      const eB = entryBLookup.get(s.storm_id);
      if (eB == null) continue;
      if (eC <= eB) continue;
      const infoC = getTransitInfo(s, boxC);
      if (infoC) stormsABC.push(infoC);
    }
  }

  const probBA = stormsA.length > 0 ? stormsAB.length / stormsA.length : 0;
  const probCAB =
    stormsABC != null && stormsAB.length > 0
      ? stormsABC.length / stormsAB.length
      : null;

  const effYears = effectiveYears(stormsA, f);

  stormsA.sort((a, b) => b.year - a.year);
  stormsAB.sort((a, b) => b.year - a.year);
  if (stormsABC) stormsABC.sort((a, b) => b.year - a.year);

  return {
    prob_b_given_a: round(probBA, 4),
    prob_b_given_a_pct: round(probBA * 100, 1),
    prob_c_given_ab: probCAB == null ? null : round(probCAB, 4),
    prob_c_given_ab_pct: probCAB == null ? null : round(probCAB * 100, 1),
    count_a: stormsA.length,
    count_ab: stormsAB.length,
    count_abc: stormsABC == null ? null : stormsABC.length,
    storms_a: stormsA,
    storms_ab: stormsAB,
    storms_abc: stormsABC,
    breakdown_a: tallyBreakdown(stormsA),
    breakdown_ab: tallyBreakdown(stormsAB),
    breakdown_abc: stormsABC == null ? null : tallyBreakdown(stormsABC),
    annual_frequency_a: effYears > 0 ? round(stormsA.length / effYears, 2) : 0,
    annual_frequency_ab: effYears > 0 ? round(stormsAB.length / effYears, 2) : 0,
    annual_frequency_abc:
      stormsABC == null
        ? null
        : effYears > 0
          ? round(stormsABC.length / effYears, 2)
          : 0,
  };
}
